import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import Spacer from '../Spacer';
import Label from './Label';

const Container = styled.div<{ width: string }>`
  position: relative;
  width: ${(props) => props.width};
`;

const InputContainer = styled.div`
  outline: none;
`;

const CurrentOptionBorder = styled.div`
  padding: 5px;
  border-radius: 6px;
  transition: all 0.2s;
  cursor: pointer;
  outline: none;
`;

const CurrentOption = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 6px 6px 14px;
  transition: all 0.2s;
  border-radius: 6px;
  background: white;
  align-items: center;
`;

const CurrentOptionText = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const DropdownButton = styled.div`
  padding: 5px 7px;
  background: #f6f6ff;
  color: #aeb1fc;
  border-radius: 5px;
  transition: all 0.2s;
`;

const DropdownMenu = styled.div<{ maxHeight: string }>`
  position: absolute;
  width: calc(100% - 14px);
  margin: 7px;
  background: white;
  border-radius: 6px;
  box-shadow: 0px 6px 18px #8874d624;
  border: 1px solid #f6f6ff;
  z-index: 1;
  max-height: ${(props) => props.maxHeight};
  overflow-y: scroll;
`;

const DropdownOption = styled.div`
  padding: 10px;
  font-weight: 600;
  font-size: 14px;
  color: #9092aa;
  cursor: pointer;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #f6f6ff;
`;

const DropLabel = styled.div`
  display: flex;
  justify-content: space-between;
`;

type DropState = 'active' | 'inactive';

const STATES: {
  [key in DropState]: {
    borderColor: string;
    backgroundColor: string;
    buttonBackground: string;
    buttonColor: string;
  };
} = {
  active: {
    borderColor: '#AEB1FC',
    backgroundColor: '#f6f6ff',
    buttonBackground: '#f6f6ff',
    buttonColor: '#AEB1FC',
  },
  inactive: {
    borderColor: '#e3e4e5',
    backgroundColor: 'transparent',
    buttonBackground: 'transparent',
    buttonColor: '#C7C7C7',
  },
};

export type DropdownOptionType = { label: string; value: any };

type Props = {
  label?: string;
  options: DropdownOptionType[];
  defaultOption: DropdownOptionType;
  onChange: (selected: DropdownOptionType) => void;
  maxDropHeight?: string;
  width?: string;
  extraLabelInfo?: React.ReactNode;
};

function Dropdown({
  label,
  extraLabelInfo,
  options,
  defaultOption,
  onChange,
  maxDropHeight = '250px',
  width = '200px',
}: Props) {
  const [dropState, setDropState] = useState<DropState>('inactive');
  const [currentOption, setCurrentOption] = useState(defaultOption);

  const inputCont = useRef(null);
  return (
    <Container width={width}>
      {label && (
        <DropLabel>
          <Label>{label}</Label>
          {extraLabelInfo}
        </DropLabel>
      )}
      <Spacer vertical spacing={1} />
      <InputContainer
        onFocus={() => setDropState('active')}
        onBlur={() => setDropState('inactive')}
        tabIndex={-1}
        ref={inputCont}
      >
        <CurrentOptionBorder
          style={{ background: STATES[dropState].backgroundColor }}
        >
          <CurrentOption
            style={{ border: `1px solid ${STATES[dropState].borderColor}` }}
          >
            <CurrentOptionText>{currentOption.label}</CurrentOptionText>
            <DropdownButton
              style={{
                background: STATES[dropState].buttonBackground,
                color: STATES[dropState].buttonColor,
              }}
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </DropdownButton>
          </CurrentOption>
        </CurrentOptionBorder>
        {dropState === 'active' && (
          <DropdownMenu maxHeight={maxDropHeight}>
            {options.map((option, index) => (
              <>
                <DropdownOption
                  onClick={() => {
                    setCurrentOption(option);
                    setDropState('inactive');
                    if (inputCont.current) {
                      const inp: any = inputCont.current;
                      inp.blur();
                    }
                    onChange(option);
                  }}
                >
                  {option.label}
                </DropdownOption>
                {options.length - 1 !== index && <Divider />}
              </>
            ))}
          </DropdownMenu>
        )}
      </InputContainer>
    </Container>
  );
}

export default Dropdown;
